// N3 (2026-05-08, ALGORITHM_FLOW v0.49) — '현재재고' 탭 정정.
//
//   본질 design v0.49 — 사용자 dictation:
//     - "현재재고 탭 = provider_item_mirror = BoxHero 외부 재고 / 품목 / 현재 수량 / 입고/출고 로그 / 동기화 상태"
//     - "모든위치 클릭 → BoxHero 등록 위치 list scroll → 위치 선택 → 그 위치 품목/수량"
//     - "50개씩 / 100개씩 영역 영역 폐기 = 재료 페이지처럼 infinite scroll"
//     - "4번 이미지 = 박스히어로 정보 X = 외부 박스히어로 매칭 의무 (= 최소 / 일소비 / 회전 / 상태 column)"
//     - "입출고 로그 = 외부 박스히어로 로그가 나와야지" + "품목 미상" → API LEFT JOIN 정정
//
//   변경:
//     1. 위치 selector — '모든위치' 기본 button + popover (= dropdown) → BoxHero 위치 list scroll
//     2. infinite scroll — useInfiniteQuery + IntersectionObserver (= 50/100 dropdown 폐기)
//     3. 4 column = mirror.inventoryMinStock / dailyConsumption / turnoverRate / inventoryStatus (= API LEFT JOIN)
//     4. MovementLog '품목 미상' → API name LEFT JOIN

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  boxheroIntegrationControllerGetStatus,
  boxheroIntegrationControllerResync,
  inventoryControllerItemsSummary,
  inventoryControllerListItems,
  inventoryControllerListLocations,
  inventoryControllerTxLogs,
} from '@dvvb/sdk';
import {
  AlertBanner,
  Badge,
  Button,
  Card,
  EmptyState,
  ItemThumb,
  Modal,
  StatusDot,
  TextInput,
  useToast,
} from '../../components/primitives';
import { unwrap, ApiCallError } from '../../lib/envelope';
import { useAuth } from '../../auth/AuthContext';
import { queryClient } from '../../lib/queryClient';
import { useViewport } from '../../lib/useViewport';
import type {
  BoxheroStatusResponse,
  InventoryItemRow,
  InventoryLocationRow,
} from '../../types/api';

const KPI_COLS = 'repeat(4, 1fr)';
const TABLE_COLS = '40px minmax(160px,1.4fr) 90px 80px 80px 110px 90px';
const FIRST_PAGE_LIMIT = 70;
const NEXT_PAGE_LIMIT = 30;

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const diffMs = Date.now() - t;
  if (diffMs < 0) return '방금';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}초 전`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.floor(hr / 24);
  return `${day}일 전`;
}

function formatNumber(n: number): string {
  return n.toLocaleString('ko-KR', { maximumFractionDigits: 4 });
}

function formatWon(n: number): string {
  return '₩' + Math.round(n).toLocaleString('ko-KR');
}

// BoxHero attrs 영역 영역 unit 추출 (= 단위 표시 영역 영역).
type BoxheroAttrs = Record<string, unknown> | unknown[] | null;

function extractUnit(attrs: BoxheroAttrs): string {
  const candidates = ['unit', 'unit_name', 'uom', '단위'];
  if (!attrs) return '';
  if (Array.isArray(attrs)) {
    const lower = candidates.map((c) => c.toLowerCase());
    for (const entry of attrs) {
      if (!entry || typeof entry !== 'object') continue;
      const e = entry as Record<string, unknown>;
      const keyish = String(e.name ?? e.key ?? e.attr_name ?? '').toLowerCase();
      if (lower.includes(keyish)) {
        const v = e.value ?? e.val;
        if (typeof v === 'string' && v.trim().length > 0) return v.trim();
      }
    }
    return '';
  }
  for (const k of candidates) {
    const v = (attrs as Record<string, unknown>)[k];
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  }
  return '';
}

export function CurrentTab() {
  const auth = useAuth();
  const toast = useToast();
  const isOwner = auth.role === 'owner';

  const statusQ = useQuery({
    queryKey: ['boxhero', 'status'],
    queryFn: async () => {
      const r = await boxheroIntegrationControllerGetStatus();
      return unwrap<BoxheroStatusResponse>(r);
    },
    staleTime: 30_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  if (statusQ.isLoading && !statusQ.data) {
    return (
      <Card style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}>
        재고 프로그램 연결 상태를 확인하는 중…
      </Card>
    );
  }

  if (statusQ.isError || !statusQ.data) {
    const err = statusQ.error as ApiCallError | undefined;
    return (
      <AlertBanner
        tone="danger"
        message={`재고 프로그램 상태를 불러오지 못했습니다: ${err?.message ?? '알 수 없는 오류'}`}
      />
    );
  }

  const status = statusQ.data;

  if (!status.connected) {
    return <NotConnected isOwner={isOwner} />;
  }

  return (
    <Connected
      status={status}
      isOwner={isOwner}
      onResync={async () => {
        try {
          const r = await boxheroIntegrationControllerResync();
          unwrap(r);
          toast.success('재동기화를 시작했습니다.');
          await queryClient.invalidateQueries({ queryKey: ['boxhero', 'status'] });
          await queryClient.invalidateQueries({ queryKey: ['inventory', 'items'] });
        } catch (e) {
          const err = e as ApiCallError;
          toast.error(err.message ?? '재동기화 실패');
        }
      }}
    />
  );
}

function NotConnected({ isOwner }: { isOwner: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <AlertBanner
        tone="info"
        message="재고 프로그램이 아직 연결되지 않았어요. 설정 > 연동에서 토큰을 등록하면 재고가 자동으로 동기화됩니다."
      />
      {isOwner ? (
        <Link
          to="/settings"
          style={{ alignSelf: 'flex-start', textDecoration: 'none' }}
        >
          <Button variant="primary" size="md">
            설정 &gt; 연동으로 가기
          </Button>
        </Link>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>
          owner 가 토큰을 등록할 때까지 재고 정보가 표시되지 않습니다.
        </div>
      )}
    </div>
  );
}

interface ConnectedProps {
  status: BoxheroStatusResponse;
  isOwner: boolean;
  onResync: () => void | Promise<void>;
}

interface ItemsEnvelope {
  items: InventoryItemRow[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}

interface LocationsEnvelope {
  items: InventoryLocationRow[];
  count: number;
}

function Connected({ status, isOwner, onResync }: ConnectedProps) {
  const viewport = useViewport();
  const isMobile = viewport === 'mobile';
  const isCompact = viewport !== 'desktop';

  const [rawQ, setRawQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  // N3.1 (2026-05-09) — 위치 selector. null = '모든위치'.
  //   value 영역 영역 BoxHero raw bigint string (= mirror.boxheroLocationId).
  //   옛 N3 영역 영역 dvvb location.id (= uuid) 영역 영역 본질 위반 정정.
  const [selectedBoxheroLocId, setSelectedBoxheroLocId] = useState<string | null>(null);
  const [showLocPopover, setShowLocPopover] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(rawQ.trim()), 300);
    return () => clearTimeout(t);
  }, [rawQ]);

  // 위치 list — popover source.
  const locationsQ = useQuery({
    queryKey: ['inventory', 'locations'],
    queryFn: async () => {
      const r = await inventoryControllerListLocations();
      return unwrap<LocationsEnvelope>(r);
    },
    staleTime: 60_000,
  });

  // infinite scroll items.
  const itemsQ = useInfiniteQuery({
    queryKey: ['inventory', 'items', { q: debouncedQ, locationId: selectedBoxheroLocId }],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }): Promise<ItemsEnvelope> => {
      const r = await inventoryControllerListItems({
        query: {
          limit: String(pageParam ? NEXT_PAGE_LIMIT : FIRST_PAGE_LIMIT),
          ...(pageParam ? { cursor: pageParam } : {}),
          ...(debouncedQ ? { q: debouncedQ } : {}),
          // N3.1 (2026-05-09) — locationId = BoxHero raw bigint string (= mirror.boxheroLocationId).
          ...(selectedBoxheroLocId ? { locationId: selectedBoxheroLocId } : {}),
        } as never,
      });
      return unwrap<ItemsEnvelope>(r);
    },
    getNextPageParam: (last) => (last.has_more ? last.cursor : null),
  });

  const items = useMemo(() => {
    const pages = itemsQ.data?.pages ?? [];
    return pages.flatMap((p) => p.items ?? []);
  }, [itemsQ.data]);

  const summaryQ = useQuery({
    queryKey: ['inventory', 'items-summary', { q: debouncedQ }],
    queryFn: async () => {
      const r = await inventoryControllerItemsSummary({
        query: { ...(debouncedQ ? { q: debouncedQ } : {}) } as never,
      });
      return unwrap<{ total: number; totalValue: number }>(r);
    },
  });

  const summary = summaryQ.data;
  const totalItems = summary?.total ?? 0;
  const totalValue = summary?.totalValue ?? 0;

  // IntersectionObserver — 무한 스크롤 sentinel.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((e) => e.isIntersecting);
        if (isVisible && itemsQ.hasNextPage && !itemsQ.isFetchingNextPage) {
          itemsQ.fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [itemsQ]);

  const lastSyncIso = items.reduce<string | null>((latest, it) => {
    if (!latest) return it.lastSyncedAt;
    return new Date(it.lastSyncedAt).getTime() > new Date(latest).getTime()
      ? it.lastSyncedAt
      : latest;
  }, null);
  const lastSyncRel = formatRelative(lastSyncIso ?? status.lastSyncAt);

  const syncTone =
    status.syncStatus === 'active'
      ? 'success'
      : status.syncStatus === 'error'
        ? 'danger'
        : status.syncStatus === 'paused'
          ? 'warning'
          : 'muted';

  // N3.1 (2026-05-09) — selectedBoxheroLocId 영역 영역 mirror row 영역 영역 lookup.
  const selectedLocation =
    locationsQ.data?.items.find((l) => l.boxheroLocationId === selectedBoxheroLocId) ?? null;
  const locButtonLabel = selectedLocation ? selectedLocation.name : '모든위치';

  // 부족 카운트 (= matched inventory.minStock 기반).
  const lowStock = items.filter((it) => {
    const min = it.inventoryMinStock;
    return min != null && min > 0 && it.quantity < min;
  }).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
      {/* 상태 + 재동기화 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          fontSize: 12,
          color: 'var(--fg-3)',
        }}
      >
        <StatusDot tone={syncTone as 'success' | 'warning' | 'danger' | 'muted'} />
        <span>
          마지막 동기화{' '}
          <span style={{ color: 'var(--fg-2)', fontVariantNumeric: 'tabular-nums' }}>
            {lastSyncRel}
          </span>
        </span>
        {status.teamName && (
          <>
            <span style={{ color: 'var(--fg-4)' }}>·</span>
            <span>재고 프로그램 팀: {status.teamName}</span>
          </>
        )}
        {status.webhookStatus === 'manual_required' && (
          <>
            <span style={{ color: 'var(--fg-4)' }}>·</span>
            <Badge variant="warning">webhook 수동 등록 필요</Badge>
          </>
        )}
        <div style={{ flex: 1 }} />
        <Button
          variant="secondary"
          size="sm"
          disabled={!isOwner}
          onClick={onResync}
          title={isOwner ? '수동 재동기화' : '권한 없음 (owner 만)'}
        >
          재동기화
        </Button>
      </div>

      {/* KPI */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isCompact ? 'repeat(2, minmax(0, 1fr))' : KPI_COLS,
          gap: 12,
        }}
      >
        <InvKpi
          label={debouncedQ ? `검색 결과` : '총 품목 수'}
          value={summaryQ.isLoading ? '—' : `${totalItems}`}
          sub={debouncedQ ? `"${debouncedQ}" 일치` : '재고 프로그램 등록'}
        />
        <InvKpi
          label="재고 부족"
          value={`${lowStock}`}
          sub={lowStock > 0 ? '최소치 이하' : '안전'}
          tone={lowStock > 0 ? 'warning' : 'success'}
        />
        <InvKpi
          label="총 재고가치"
          value={summaryQ.isLoading ? '—' : formatWon(totalValue)}
          sub="quantity × cost"
        />
        <InvKpi
          label="마지막 동기화"
          value={lastSyncRel}
          sub={status.syncStatus}
          tone={syncTone === 'success' ? 'success' : syncTone === 'danger' ? 'danger' : undefined}
        />
      </div>

      {/* 메인 그리드 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isCompact ? '1fr' : '1.4fr 1fr',
          gap: 16,
          minWidth: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <SectionTitle title="재고 현황" sub={`${locButtonLabel} · 재고 프로그램 ${totalItems}개 품목`} />

          {/* 검색 + 위치 selector */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 10,
              flexWrap: 'wrap',
              position: 'relative',
            }}
          >
            <div style={{ flex: '1 1 220px', minWidth: isMobile ? '100%' : 180, maxWidth: 360 }}>
              <TextInput
                type="search"
                value={rawQ}
                onChange={setRawQ}
                placeholder="품목명 · SKU · 바코드 · 카테고리 검색"
              />
            </div>
            {/* 위치 selector */}
            <div style={{ position: 'relative' }}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowLocPopover((s) => !s)}
              >
                {locButtonLabel} ▾
              </Button>
              {showLocPopover && (
                <LocationPopover
                  locations={locationsQ.data?.items ?? []}
                  selectedBoxheroLocId={selectedBoxheroLocId}
                  onSelect={(boxheroLocId) => {
                    setSelectedBoxheroLocId(boxheroLocId);
                    setShowLocPopover(false);
                  }}
                  onClose={() => setShowLocPopover(false)}
                />
              )}
            </div>
            {rawQ && (
              <Button variant="ghost" size="sm" onClick={() => setRawQ('')}>
                초기화
              </Button>
            )}
          </div>

          {itemsQ.isLoading && items.length === 0 ? (
            <Card style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}>
              품목을 불러오는 중…
            </Card>
          ) : itemsQ.isError ? (
            <AlertBanner
              tone="danger"
              message={`품목을 불러오지 못했습니다: ${(itemsQ.error as ApiCallError | undefined)?.message ?? '알 수 없는 오류'}`}
            />
          ) : items.length === 0 ? (
            <EmptyState
              icon="package"
              title={debouncedQ ? '검색 결과가 없습니다' : '동기화된 품목이 없습니다'}
              description={
                debouncedQ
                  ? `"${debouncedQ}" 와 일치하는 품목이 없습니다. 다른 키워드로 검색해보세요.`
                  : '재고 프로그램에 품목이 등록되어 있지 않거나, 첫 동기화가 아직 완료되지 않았습니다. 재동기화를 눌러주세요.'
              }
            />
          ) : (
            <>
              <InventoryGrid items={items} />
              {/* infinite scroll sentinel */}
              <div ref={sentinelRef} style={{ height: 1 }} />
              {itemsQ.isFetchingNextPage && (
                <div style={{ padding: 12, textAlign: 'center', color: 'var(--fg-3)', fontSize: 12 }}>
                  더 불러오는 중…
                </div>
              )}
              {!itemsQ.hasNextPage && items.length >= FIRST_PAGE_LIMIT && (
                <div style={{ padding: 12, textAlign: 'center', color: 'var(--fg-4)', fontSize: 12 }}>
                  마지막 품목까지 불러왔습니다.
                </div>
              )}
            </>
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <SectionTitle title="입출고 로그" sub="자동 차감 · 입고 · 실사" />
          <MovementLog />
        </div>
      </div>
    </div>
  );
}

function LocationPopover({
  locations,
  selectedBoxheroLocId,
  onSelect,
  onClose,
}: {
  locations: InventoryLocationRow[];
  selectedBoxheroLocId: string | null;
  onSelect: (boxheroLocId: string | null) => void;
  onClose: () => void;
}) {
  // backdrop click close.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-loc-popover]')) onClose();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onClose]);
  return (
    <div
      data-loc-popover
      style={{
        position: 'absolute',
        top: 'calc(100% + 4px)',
        right: 0,
        zIndex: 10,
        width: 220,
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: 280,
        overflow: 'auto',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-base)',
        borderRadius: 6,
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        padding: 4,
      }}
    >
      <button
        type="button"
        onClick={() => onSelect(null)}
        style={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          padding: '8px 10px',
          background: selectedBoxheroLocId == null ? 'var(--bg-subtle)' : 'transparent',
          border: 'none',
          borderRadius: 4,
          fontSize: 13,
          color: 'var(--fg-1)',
          cursor: 'pointer',
        }}
      >
        모든위치
      </button>
      {locations.length === 0 ? (
        <div style={{ padding: '8px 10px', fontSize: 12, color: 'var(--fg-4)' }}>
          BoxHero 위치가 동기화되지 않았습니다.
        </div>
      ) : (
        locations.map((loc) => (
          <button
            key={loc.id}
            type="button"
            // N3.1 (2026-05-09) — onSelect = BoxHero raw bigint string (= mirror.boxheroLocationId).
            onClick={() => onSelect(loc.boxheroLocationId)}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '8px 10px',
              background:
                selectedBoxheroLocId === loc.boxheroLocationId
                  ? 'var(--bg-subtle)'
                  : 'transparent',
              border: 'none',
              borderRadius: 4,
              fontSize: 13,
              color: 'var(--fg-1)',
              cursor: 'pointer',
            }}
          >
            {loc.name}
          </button>
        ))
      )}
    </div>
  );
}

function SectionTitle({ title, sub }: { title: string; sub?: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 10,
        marginBottom: 10,
        flexWrap: 'wrap',
      }}
    >
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--fg-1)' }}>{title}</div>
        {sub && (
          <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 3 }}>{sub}</div>
        )}
      </div>
    </div>
  );
}

/**
 * N3 (2026-05-08) — InventoryGrid 정정.
 *   4 column = mirror.inventoryMinStock / dailyConsumption / turnoverRate / inventoryStatus (= API LEFT JOIN).
 *   미매칭 row 영역 영역 '—'.
 */
function InventoryGrid({ items }: { items: InventoryItemRow[] }) {
  const [filter, setFilter] = useState<'all' | 'low' | 'critical'>('all');
  const viewport = useViewport();
  const isCompact = viewport !== 'desktop';
  const cols = isCompact ? '40px minmax(0, 1fr) 90px 60px' : TABLE_COLS;

  const enriched = items.map((it) => {
    const min = it.inventoryMinStock;
    const ratio = min != null && min > 0 ? it.quantity / min : null;
    // API status 우선 → 미매칭 시 ratio 계산.
    let status: 'ok' | 'low' | 'critical' | 'unknown';
    if (it.inventoryStatus === 'critical') status = 'critical';
    else if (it.inventoryStatus === 'low') status = 'low';
    else if (it.inventoryStatus === 'ok' || it.inventoryStatus === 'active') status = 'ok';
    else if (ratio == null) status = 'unknown';
    else if (ratio < 0.5) status = 'critical';
    else if (ratio < 1) status = 'low';
    else status = 'ok';
    return { item: it, min, status };
  });

  const counts = {
    all: enriched.length,
    low: enriched.filter((e) => e.status === 'low' || e.status === 'critical').length,
    critical: enriched.filter((e) => e.status === 'critical').length,
  };

  const filtered = enriched.filter((e) => {
    if (filter === 'all') return true;
    if (filter === 'low') return e.status === 'low' || e.status === 'critical';
    if (filter === 'critical') return e.status === 'critical';
    return true;
  });

  const tabs: Array<{ id: 'all' | 'low' | 'critical'; label: string; count: number }> = [
    { id: 'all', label: '전체', count: counts.all },
    { id: 'low', label: '부족', count: counts.low },
    { id: 'critical', label: '위험', count: counts.critical },
  ];

  const statusMap = {
    ok: { label: '정상', color: 'var(--success)', bg: 'var(--success-bg)' },
    low: { label: '부족', color: 'var(--warning)', bg: 'var(--warning-bg)' },
    critical: { label: '위험', color: 'var(--danger)', bg: 'var(--danger-bg)' },
    unknown: { label: '—', color: 'var(--fg-4)', bg: 'var(--bg-subtle)' },
  } as const;

  return (
    <Card style={{ padding: 0 }}>
      {/* 필터 탭 row */}
      <div
        style={{
          padding: '10px 14px',
          borderBottom: '1px solid var(--border-weak)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap',
        }}
      >
        {tabs.map((t) => {
          const on = t.id === filter;
          return (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '5px 10px',
                fontSize: 12,
                fontWeight: on ? 600 : 500,
                color: on ? 'var(--fg-1)' : 'var(--fg-3)',
                background: on ? 'var(--bg-subtle)' : 'transparent',
                border: on ? '1px solid var(--border-base)' : '1px solid transparent',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              {t.label}
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '0 6px',
                  borderRadius: 999,
                  minWidth: 18,
                  textAlign: 'center',
                  background: on ? 'var(--bg-surface)' : 'var(--bg-subtle)',
                  color: on ? 'var(--fg-2)' : 'var(--fg-3)',
                }}
              >
                {t.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* 헤더 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: cols,
          gap: 10,
          padding: '8px 14px',
          background: 'var(--bg-subtle)',
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--fg-3)',
          textTransform: 'uppercase',
          letterSpacing: 0,
        }}
      >
        {isCompact ? (
          <>
            <div />
            <div>품목</div>
            <div style={{ textAlign: 'right' }}>현재</div>
            <div style={{ textAlign: 'right' }}>상태</div>
          </>
        ) : (
          <>
            <div />
            <div>품목</div>
            <div style={{ textAlign: 'right' }}>현재</div>
            <div style={{ textAlign: 'right' }}>최소</div>
            <div style={{ textAlign: 'right' }}>일 소비</div>
            <div style={{ textAlign: 'right' }}>회전</div>
            <div style={{ textAlign: 'right' }}>상태</div>
          </>
        )}
      </div>

      {/* 행 */}
      {filtered.length === 0 ? (
        <div
          style={{
            padding: 24,
            color: 'var(--fg-3)',
            fontSize: 13,
            textAlign: 'center',
          }}
        >
          필터 조건에 맞는 품목이 없습니다.
        </div>
      ) : (
        filtered.map(({ item, min, status }, i) => {
          const s = statusMap[status];
          const unit = extractUnit(item.attrs);
          const pct =
            min != null && min > 0 ? Math.min(100, (item.quantity / (min * 2)) * 100) : 0;
          return (
            <div
              key={item.id}
              style={{
                display: 'grid',
                gridTemplateColumns: cols,
                gap: 10,
                padding: '11px 14px',
                alignItems: 'center',
                borderTop: i === 0 ? 'none' : '1px solid var(--border-weak)',
                fontSize: 13,
              }}
            >
              <ItemThumb name={item.name} imageUrl={item.photoUrl} />

              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 600,
                    color: 'var(--fg-1)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.name}
                </div>
                {(item.sku || item.category) && (
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--fg-4)',
                      marginTop: 2,
                      display: 'flex',
                      gap: 6,
                    }}
                  >
                    {item.sku && <span>SKU: {item.sku}</span>}
                    {item.category && <span>· {item.category}</span>}
                  </div>
                )}
                {min != null && (
                  <div
                    style={{
                      height: 3,
                      marginTop: 6,
                      borderRadius: 999,
                      background: 'var(--bg-subtle)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${pct}%`,
                        background: s.color,
                        borderRadius: 999,
                      }}
                    />
                  </div>
                )}
              </div>

              <div
                style={{
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                  color: status === 'unknown' ? 'var(--fg-1)' : s.color,
                  fontWeight: 600,
                }}
              >
                {formatNumber(item.quantity)}
                {unit && (
                  <span
                    style={{
                      fontSize: 11,
                      color: 'var(--fg-3)',
                      fontWeight: 400,
                      marginLeft: 2,
                    }}
                  >
                    {unit}
                  </span>
                )}
              </div>

              {/* 최소 — N3: API LEFT JOIN inventoryMinStock */}
              {!isCompact && (
                <div
                  style={{
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    color: 'var(--fg-3)',
                  }}
                >
                  {min != null ? (
                    <>
                      {formatNumber(min)}
                      {unit && <span style={{ fontSize: 11, marginLeft: 2 }}>{unit}</span>}
                    </>
                  ) : (
                    <span style={{ color: 'var(--fg-4)' }}>—</span>
                  )}
                </div>
              )}

              {/* 일 소비 — N3: API LEFT JOIN dailyConsumption (= 별 phase 영역 영역 null) */}
              {!isCompact && (
                <div
                  style={{
                    textAlign: 'right',
                    color: item.dailyConsumption != null ? 'var(--fg-2)' : 'var(--fg-4)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {item.dailyConsumption != null ? formatNumber(item.dailyConsumption) : '—'}
                </div>
              )}

              {/* 회전 — N3: API LEFT JOIN turnoverRate (= 별 phase 영역 영역 null) */}
              {!isCompact && (
                <div
                  style={{
                    textAlign: 'right',
                    color: item.turnoverRate != null ? 'var(--fg-2)' : 'var(--fg-4)',
                    fontSize: 12,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {item.turnoverRate != null ? formatNumber(item.turnoverRate) : '—'}
                </div>
              )}

              <div style={{ textAlign: 'right' }}>
                {status === 'unknown' ? (
                  <span style={{ fontSize: 11, color: 'var(--fg-4)' }}>—</span>
                ) : (
                  <Badge variant={status === 'critical' ? 'danger' : status === 'low' ? 'warning' : 'success'}>
                    {s.label}
                  </Badge>
                )}
              </div>
            </div>
          );
        })
      )}
    </Card>
  );
}

interface TxLogItem {
  id: string;
  type: string;
  source: string;
  processedAt: string;
  itemName: string | null;
  quantity: number | null;
  note: string | null;
  payload?: Record<string, unknown> | null;
}

interface TxLogsEnvelope {
  items: TxLogItem[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}

function MovementLog() {
  const [detail, setDetail] = useState<TxLogItem | null>(null);

  const txQ = useQuery({
    queryKey: ['inventory', 'tx-logs', { limit: 30 }],
    queryFn: async () => {
      const r = await inventoryControllerTxLogs({
        query: { limit: '30' } as never,
      });
      return unwrap<TxLogsEnvelope>(r);
    },
  });

  if (txQ.isLoading) {
    return (
      <Card style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}>
        입출고 로그를 불러오는 중…
      </Card>
    );
  }

  if (txQ.isError) {
    const err = txQ.error as ApiCallError | undefined;
    return (
      <AlertBanner
        tone="danger"
        message={`입출고 로그를 불러오지 못했습니다: ${err?.message ?? '알 수 없는 오류'}`}
      />
    );
  }

  const items = txQ.data?.items ?? [];
  if (items.length === 0) {
    return (
      <EmptyState
        icon="activity"
        title="아직 입출고 기록이 없습니다"
        description="레시피 기반 자동 차감 / 재고 프로그램 입출고 webhook 이 처리되면 여기에 표시됩니다."
      />
    );
  }

  return (
    <Card style={{ padding: 0 }}>
      {items.map((it, i) => {
        const meta = txTypeMeta(it.type);
        const isNeg = (it.quantity ?? 0) < 0;
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => setDetail(it)}
            style={{
              display: 'grid',
              gridTemplateColumns: '8px 1fr auto',
              gap: 10,
              padding: '10px 14px',
              alignItems: 'center',
              borderTop: i === 0 ? 'none' : '1px solid var(--border-weak)',
              fontSize: 13,
              width: '100%',
              background: 'transparent',
              border: 'none',
              borderTopWidth: i === 0 ? 0 : 1,
              borderTopStyle: 'solid',
              borderTopColor: 'var(--border-weak)',
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: 'inherit',
              color: 'inherit',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                'var(--bg-subtle)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: meta.color,
                flexShrink: 0,
              }}
            />
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--fg-1)',
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '1px 6px',
                    borderRadius: 4,
                    color: meta.color,
                    background: meta.bg,
                    textTransform: 'uppercase',
                    letterSpacing: 0,
                  }}
                >
                  {meta.label}
                </span>
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {/* N3 (2026-05-08) — API LEFT JOIN 영역 영역 itemName 영역 영역 BoxHero name 영역 영역. */}
                  {it.itemName ?? '품목 미상'}
                </span>
                {it.quantity != null && (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: isNeg ? 'var(--danger)' : 'var(--success)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {isNeg ? '' : '+'}
                    {formatNumber(it.quantity)}
                  </span>
                )}
              </div>
              {(it.note || it.source) && (
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--fg-3)',
                    marginTop: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {it.note ?? `source: ${it.source}`}
                </div>
              )}
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--fg-4)',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              }}
            >
              {formatRelative(it.processedAt)}
            </div>
          </button>
        );
      })}
      <TxLogDetailModal item={detail} onClose={() => setDetail(null)} />
    </Card>
  );
}

function TxLogDetailModal({
  item,
  onClose,
}: {
  item: TxLogItem | null;
  onClose: () => void;
}) {
  if (!item) {
    return (
      <Modal open={false} onClose={onClose} title="입출고 상세">
        <div />
      </Modal>
    );
  }
  const meta = txTypeMeta(item.type);
  const isNeg = (item.quantity ?? 0) < 0;
  const ts = new Date(item.processedAt);
  const tsLabel = Number.isFinite(ts.getTime())
    ? ts.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
    : item.processedAt;

  return (
    <Modal
      open={true}
      onClose={onClose}
      title="입출고 상세"
      size="md"
      footer={
        <Button variant="secondary" onClick={onClose}>
          닫기
        </Button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <DetailRow label="유형">
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: 4,
              color: meta.color,
              background: meta.bg,
              textTransform: 'uppercase',
              letterSpacing: 0,
            }}
          >
            {meta.label} ({item.type})
          </span>
        </DetailRow>
        <DetailRow label="시간" mono>
          {tsLabel}
        </DetailRow>
        <DetailRow label="품목">
          {item.itemName ?? <span style={{ color: 'var(--fg-4)' }}>품목 미상</span>}
        </DetailRow>
        <DetailRow label="수량" mono>
          {item.quantity != null ? (
            <span
              style={{
                fontWeight: 600,
                color: isNeg ? 'var(--danger)' : 'var(--success)',
              }}
            >
              {isNeg ? '' : '+'}
              {formatNumber(item.quantity)}
            </span>
          ) : (
            <span style={{ color: 'var(--fg-4)' }}>—</span>
          )}
        </DetailRow>
        <DetailRow label="사유">
          {item.note ?? <span style={{ color: 'var(--fg-4)' }}>—</span>}
        </DetailRow>
        <DetailRow label="소스" mono>
          {item.source}
        </DetailRow>

        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--fg-3)',
              textTransform: 'uppercase',
              letterSpacing: 0,
              marginBottom: 4,
            }}
          >
            Raw payload
          </div>
          <pre
            style={{
              margin: 0,
              padding: 10,
              background: 'var(--bg-subtle)',
              border: '1px solid var(--border-weak)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              color: 'var(--fg-2)',
              maxHeight: 280,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {item.payload
              ? JSON.stringify(item.payload, null, 2)
              : '(payload 없음)'}
          </pre>
        </div>
      </div>
    </Modal>
  );
}

function DetailRow({
  label,
  children,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '80px 1fr',
        gap: 12,
        alignItems: 'baseline',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--fg-3)',
          textTransform: 'uppercase',
          letterSpacing: 0,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--fg-1)',
          fontFamily: mono ? 'var(--font-mono)' : 'var(--font-sans)',
          fontVariantNumeric: mono ? 'tabular-nums' : 'normal',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function txTypeMeta(type: string): { label: string; color: string; bg: string } {
  const t = type.toLowerCase();
  if (t === 'in' || t === 'inbound' || t === 'recv') {
    return { label: '입고', color: 'var(--success)', bg: 'var(--success-bg)' };
  }
  if (t === 'out' || t === 'sale_out' || t === 'sale-out' || t === 'outbound') {
    return { label: '출고', color: 'var(--danger)', bg: 'var(--danger-bg)' };
  }
  if (t === 'adjust' || t === 'adjustment' || t === 'audit') {
    return { label: '조정', color: 'var(--warning)', bg: 'var(--warning-bg)' };
  }
  if (t === 'transfer') {
    return { label: '이동', color: 'var(--info)', bg: 'var(--info-bg)' };
  }
  return { label: type.toUpperCase().slice(0, 6), color: 'var(--fg-3)', bg: 'var(--bg-subtle)' };
}

function InvKpi({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'success' | 'warning' | 'danger';
}) {
  const accent =
    tone === 'success'
      ? 'var(--success)'
      : tone === 'warning'
        ? 'var(--warning)'
        : tone === 'danger'
          ? 'var(--danger)'
          : 'var(--fg-1)';
  return (
    <Card style={{ padding: 16 }}>
      <div style={{ fontSize: 12, color: 'var(--fg-3)', marginBottom: 6 }}>{label}</div>
      <div
        style={{
          fontSize: 24,
          fontWeight: 700,
          color: accent,
          letterSpacing: 0,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 4 }}>{sub}</div>
      )}
    </Card>
  );
}
